/**
 * @NOTICE OLD AND COPY-PASTED ALL TO MAKE IT COMPATIBLE WITH THE CONVERSATIONS LAYER.
 * SHOULD BE DONE DIFFERENTLY BUT COULDNT REFACTOR EVERYTHING
 */

import { getUserData } from "@/auth/getUserData";
import useChatInput from "@/composables/v3/useChatInput";
import ContactInterface from "@/interfaces/contact.interface";
// @ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
import i18n from "@/libs/i18n";
import { useChatsStore } from "@/store/useChatsStore";
import { useUploadAttachmentsStore } from "@/store/useUploadAttachmentsStore";
import { MediaTypeType } from "@/types/mediaType.type";
import { Ref, ref } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import { useChannelsStore } from "@/store/useChannelsStore";
import { PlatformType, ProviderType } from "@saysimple-platform/dtos";
import { escapeRegExp } from "lodash";

const templateSearchString = ref("");
const activeTabIndex = ref(null);
const activeChatTemplatesTab = ref("");
const isManageTemplatePanelActive = ref(false);
const isTemplate = ref(false);
const lastSelectedTemplate = ref({});
const isQuickReply = ref(false);
const showMediaTemplateManager = ref(false);
const hasMediaTemplateFilesUploaded = ref(false);
const availableVariables = [
    "{{agent.name}}",
    "{{agent.displayname}}",
    "{{customer.fullname}}",
    "{{customer.email}}",
    "{{customer.reference}}",
    "{{customer.jobtitle}}",
    "{{customer.company}}",
    "{{customer.phone}}",
];

type Template = {
    assetId: number | null;
    assetIsLocked: number;
    components?: Record<string, unknown>;
    content: string;
    final: string;
    id: number | null;
    mediaType: MediaTypeType;
    title: string;
    variables: any[];
    originalContent: string | null;
};

const selectedTemplate: Ref<Template> = ref({
    assetId: null,
    assetIsLocked: 0,
    components: undefined,
    content: "",
    final: "",
    id: null,
    mediaType: MediaTypeType.IMAGE,
    title: "",
    variables: [],
    originalContent: null,
});

// @ts-ignore
export default function useTemplates() {
    // @todo toast should not be placed in a composable, because it has a different scope.
    const toast = useToast();
    const channelsStore = useChannelsStore();

    const setTemplate = (template: Template): void => {
        template.variables = fetchVariablesFromContent(template.content);
        template.originalContent = template.content;

        selectedTemplate.value = template;

        // Insert directly if no variables, otherwise show new dialog with variable input fields based on value in templateVariables
        if (!template.variables) {
            setChatInputTemplate(template);
        }
    };

    const setChatInputTemplate = (template: Template): void => {
        const { message } = useChatInput();
        message.value = template.content;
        const [isValid, validationMessage] = isTemplateValid(
            template.originalContent,
            template.content,
            channelsStore.activeChatChannel
        );
        isTemplate.value = isValid;
        activeChatTemplatesTab.value = "";

        if (validationMessage) {
            toast.clear(); // Remove all existing toast before showing a new one.
            toast(
                {
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.Conversation"),
                        text: validationMessage,
                        icon: "exclamation-triangle",
                        variant: "warning",
                    },
                },
                {
                    timeout: false,
                }
            );
        }

        const { clearAttachments, fetchAsset } = useUploadAttachmentsStore();
        clearAttachments();

        // Media templates must have an attachment before we can send them
        if (template.mediaType) {
            if (!template.assetId) {
                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.templates.approved-media-templates"),
                        text: i18n.t("entities.templates.media-no-file"),
                        icon: "exclamation-triangle",
                        variant: "warning",
                    },
                });

                return;
            }

            fetchAsset(template.assetId);
            hasMediaTemplateFilesUploaded.value = true;
        }
    };

    // @ts-ignore
    const fetchVariablesFromContent = (content): Array<{ index: number; name: string; value: string }> => {
        const variables = content.match(/\{\{([^}]+)\}\}/g);

        if (!variables) {
            return;
        }

        // @ts-ignore
        return content.match(/\{\{([^}]+)\}\}/g).map((variable, index) => {
            return {
                index: index + 1,
                name: variable,
                value: variable,
            };
        });
    };

    // Minus 1, because template variables starts with 1, not 0 which is the array start index.
    // @ts-ignore
    const replaceVariablesByArray = (templateString, replacementValues): string => {
        // @ts-ignore
        return templateString.replace(/{{\s*([^}]*)\s*}}/g, (_, $1) => replacementValues[$1 - 1]);
    };

    // @ts-ignore
    const variablesToIndex = (templateString): string => {
        let index = 1;
        return templateString.replace(/{{\s*([^}]*)\s*}}/g, () => {
            const i = index;

            index++;

            return `{{${i}}}`;
        });
    };

    // @ts-ignore
    const replaceVariablesByObject = (templateString: string, replacementValuesObject): string => {
        return templateString.replace(
            /{{\s*([^}]*)\s*}}/g,
            // @ts-ignore
            (variable, $1) => replacementValuesObject[variable] ?? variable
        );
    };

    // Map the values of specific variables and return the object.
    const mapVariablesValues = (contactId?: number): Promise<Record<string, string>> => {
        const chatsStore = useChatsStore();

        // We don't have all contact data yet, so we'll do a separate request to fetch all needed data.
        return (
            axios
                .get<ContactInterface>(`/contacts/${contactId ?? chatsStore.activeChat?.contact.id}`, {
                    params: {
                        fields: "name,email,reference,jobTitle,company,phone",
                    },
                })
                // @ts-ignore
                .then((contact) => {
                    return {
                        "{{agent.name}}": getUserData().name,
                        "{{agent.displayname}}": getUserData().displayName,
                        "{{customer.fullname}}": contact.data.name,
                        "{{customer.email}}": contact.data.email,
                        "{{customer.reference}}": contact.data.reference,
                        "{{customer.jobtitle}}": contact.data.jobTitle,
                        "{{customer.company}}": contact.data.company,
                        "{{customer.phone}}": contact.data.phone,
                    };
                })
        );
    };

    const resetTemplate = (): void => {
        // @ts-ignore
        selectedTemplate.value = {};
        isTemplate.value = false;
        isQuickReply.value = false;
        hasMediaTemplateFilesUploaded.value = false;
        isManageTemplatePanelActive.value = false;
    };

    const isTemplateValid = (
        template: string,
        message: string,
        channel?: { platform?: PlatformType; provider?: ProviderType }
    ) => {
        const baseRegexString = escapeRegExp(template);

        const regexPattern = `^${baseRegexString.replace(/\\{\\{\s*([^}]*)\s*\\}\\}/g, ".*")}$`;
        const regex = new RegExp(regexPattern);

        const isMatchingSelectedTemplate = regex.test(message);

        // check additional providers requirements
        if (isMatchingSelectedTemplate && channel?.provider === "messagebird" && channel?.platform === "whatsapp") {
            const providerRegexPattern = `^${baseRegexString.replace(/\\{\\{\s*([^}]*)\s*\\}\\}/g, "(?!.*\\s{5}).*")}$`;
            const providerRegex = new RegExp(providerRegexPattern);
            if (!providerRegex.test(message)) {
                return [false, i18n.t("entities.templates.error.messagebird-consecutive-spaces")];
            }
        }

        return [isMatchingSelectedTemplate, null];
    };

    return {
        selectedTemplate,
        activeChatTemplatesTab,
        availableVariables,
        isManageTemplatePanelActive,
        isTemplate,
        isQuickReply,
        hasMediaTemplateFilesUploaded,

        // Methods
        fetchVariablesFromContent,
        mapVariablesValues,
        replaceVariablesByArray,
        replaceVariablesByObject,
        resetTemplate,
        variablesToIndex,
        setChatInputTemplate,
        isTemplateValid,

        lastSelectedTemplate,
        templateSearchString,
        activeTabIndex,
        setTemplate,
        showMediaTemplateManager,
    };
}
